import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { X, AlertTriangle, Trash2 } from 'lucide-react';

const NoteTemplateDeleteModal = ({ show, template, onClose, onSuccess }) => {
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState(null);

    // Handle delete
    const handleDelete = async () => {
        setIsDeleting(true);
        setError(null);

        try {
            const formData = new FormData();
            formData.append('id', template.note_template_id);

            const response = await fetch('/index.php/notestemplates/delete', {
                method: 'POST',
                body: formData
            });

            const result = await response.json();

            if (result.success) {
                onSuccess();
            } else {
                setError(result.message || 'Fehler beim Löschen');
            }
        } catch (error) {
            console.error('Error deleting template:', error);
            setError('Fehler beim Löschen der Notizvorlage');
        } finally {
            setIsDeleting(false);
        }
    };

    // Modal styles
    const modalHeaderStyle = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1.25rem 1.5rem',
        borderBottom: '1px solid var(--border-light, #e2e8f0)',
        background: 'var(--bg-secondary, #f5f7f9)'
    };

    const modalTitleStyle = {
        fontSize: '1.125rem',
        fontWeight: '600',
        color: 'var(--text-primary, #2c3e50)',
        margin: 0,
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
    };

    const closeButtonStyle = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '32px',
        height: '32px',
        border: 'none',
        borderRadius: '8px',
        background: 'transparent',
        color: 'var(--text-secondary, #64748b)',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
    };

    const modalBodyStyle = {
        padding: '1.5rem'
    };

    const warningBoxStyle = {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '1rem',
        padding: '1rem',
        background: 'rgba(239, 68, 68, 0.05)',
        borderRadius: '8px',
        marginBottom: '1rem'
    };

    const warningIconStyle = {
        flexShrink: 0,
        color: '#ef4444'
    };

    const warningTextStyle = {
        fontSize: '0.875rem',
        color: 'var(--text-primary, #2c3e50)',
        lineHeight: '1.5'
    };

    const templateInfoStyle = {
        padding: '1rem',
        background: 'var(--bg-secondary, #f5f7f9)',
        borderRadius: '8px',
        border: '1px solid var(--border-light, #e2e8f0)'
    };

    const templateTitleStyle = {
        fontSize: '0.875rem',
        fontWeight: '600',
        color: 'var(--text-primary, #2c3e50)',
        marginBottom: '0.25rem'
    };

    const templateCategoryStyle = {
        fontSize: '0.75rem',
        color: 'var(--text-secondary, #64748b)'
    };

    const errorStyle = {
        color: '#ef4444',
        fontSize: '0.875rem',
        padding: '0.75rem',
        background: 'rgba(239, 68, 68, 0.1)',
        borderRadius: '8px',
        marginTop: '1rem'
    };

    const modalFooterStyle = {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '0.75rem',
        padding: '1.25rem 1.5rem',
        borderTop: '1px solid var(--border-light, #e2e8f0)',
        background: 'var(--bg-secondary, #f5f7f9)'
    };

    const cancelButtonStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.625rem 1.25rem',
        border: '1px solid var(--border-medium, #cbd5e1)',
        borderRadius: '8px',
        background: 'var(--bg-card, #ffffff)',
        color: 'var(--text-primary, #2c3e50)',
        fontSize: '0.875rem',
        fontWeight: '500',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
    };

    const deleteButtonStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.625rem 1.25rem',
        border: 'none',
        borderRadius: '8px',
        background: '#ef4444',
        color: 'white',
        fontSize: '0.875rem',
        fontWeight: '500',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
    };

    return (
        <Modal
            show={show}
            onHide={onClose}
            centered
            backdrop="static"
        >
            {/* Header */}
            <div style={modalHeaderStyle}>
                <h5 style={modalTitleStyle}>
                    <AlertTriangle size={20} color="#ef4444" />
                    Notizvorlage löschen
                </h5>
                <button
                    type="button"
                    style={closeButtonStyle}
                    onClick={onClose}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-hover, #f1f5f9)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                    <X size={20} />
                </button>
            </div>

            {/* Body */}
            <div style={modalBodyStyle}>
                <div style={warningBoxStyle}>
                    <AlertTriangle size={24} style={warningIconStyle} />
                    <div style={warningTextStyle}>
                        <strong>Achtung:</strong> Das Löschen der Notizvorlage kann nicht rückgängig gemacht werden.
                    </div>
                </div>

                <div style={templateInfoStyle}>
                    <div style={templateTitleStyle}>{template.title}</div>
                    <div style={templateCategoryStyle}>
                        Kategorie: {template.category_name}
                    </div>
                </div>

                {error && <div style={errorStyle}>{error}</div>}
            </div>

            {/* Footer */}
            <div style={modalFooterStyle}>
                <button
                    type="button"
                    style={cancelButtonStyle}
                    onClick={onClose}
                    disabled={isDeleting}
                >
                    Abbrechen
                </button>
                <button
                    type="button"
                    style={{
                        ...deleteButtonStyle,
                        opacity: isDeleting ? 0.7 : 1,
                        cursor: isDeleting ? 'not-allowed' : 'pointer'
                    }}
                    onClick={handleDelete}
                    disabled={isDeleting}
                    onMouseEnter={(e) => {
                        if (!isDeleting) e.currentTarget.style.background = '#dc2626';
                    }}
                    onMouseLeave={(e) => {
                        if (!isDeleting) e.currentTarget.style.background = '#ef4444';
                    }}
                >
                    <Trash2 size={18} />
                    {isDeleting ? 'Löscht...' : 'Löschen'}
                </button>
            </div>
        </Modal>
    );
};

export default NoteTemplateDeleteModal;
